import React, { useEffect, useState } from "react";
import {
  getClientWiseEmployees,
  GetPayrollData,
  ViewPayrollHistoryApi,
} from "../../../api/request/payroll/payroll";
import UserAvatar from "../../../components/common/UserAvatar";
import GlobalTooltip from "../../../components/common/GlobalTooltip";
import View from "../../../assets/View";
import moment from "moment";
import toast from "react-hot-toast";
import columns from "../../../columns/payroll/viewPayroll";
import empColumns from "../../../columns/payroll/employeeColumns";
import SkeletonTable from "../../../components/table/SkeletonTable";
import addPayrollColumns from "../../../columns/payroll/empViewHistory";
import ModalComponent from "../../../components/modals/ModalComponent";
import { ReactComponent as Email } from "../../../assets/email.svg";
import { ReactComponent as Phone } from "../../../assets/phone.svg";
import LoadableButton from "../../../components/button/LoadableButton";
import LeftArrow from "../../../assets/LeftArrow";
import PayrollHistory from "./PayrollHistory";

const Payroll = ({ client }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [docUrl, setDocUrl] = useState([]);
  const [docOpen, setDocOpen] = useState(false);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [isEmpHistoryLoading, setIsEmpHistoryLoading] = useState(false);
  const [mode, setMode] = useState("client");
  const [employeeData, setEmployeeData] = useState([]);
  const [isEmpLoading, setIsEmpLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState([]);
  const [isEmpOpen, setIsEmpOpen] = useState(false);

  const getViewPayrollData = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetPayrollData({ client_id: client?.id });

      if (data?.status) {
        setData(
          data?.data?.map((item) => ({
            ...item,
            start_date: moment(item?.start_date).format("ll"),
            end_date: moment(item?.end_date).format("ll"),
            status: (
              <p
                className={`font-bold uppercase ${
                  item?.status === "entered" ? "text-green-500" : "text-red-500"
                }`}
              >
                {item?.status}
              </p>
            ),
            doc: (
              <div
                className={`${
                  item?.documents?.length
                    ? "text-blue-500 underline cursor-pointer"
                    : ""
                }`}
                onClick={() => {
                  if (item?.documents?.length === 1) {
                    window.open(item?.documents[0]?.path, "_blank");
                  } else if (item?.documents?.length > 1) {
                    setDocUrl(item?.documents);
                    setDocOpen(true);
                  }
                }}
              >
                {item?.documents?.length ? "View Document" : "--"}
              </div>
            ),
            completed_by_user: item?.completed_by_user ? (
              <UserAvatar
                data={{
                  name: item?.completed_by_user?.name,
                  email: item?.completed_by_user?.email,
                  profile_image: item?.profile_image,
                }}
              />
            ) : (
              "--"
            ),
            action: (
              <div className="flex gap-3">
                <GlobalTooltip title="View Payroll Employee History">
                  <button
                    onClick={() => {
                      setIsEmployeeModalOpen(true);
                      handleViewHistory(item);
                    }}
                  >
                    <View />
                  </button>
                </GlobalTooltip>
              </div>
            ),
          }))
        );
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast?.error(error?.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewHistory = async (EmpData) => {
    if (!EmpData?.id) return;
    try {
      setIsEmpHistoryLoading(true);
      const { data } = await ViewPayrollHistoryApi({ id: EmpData?.id });
      if (data?.status) {
        setEmployeeHistory(
          data?.data[0]?.payroll_data?.map((item) => {
            return {
              ...item,
              email: (
                <div>
                  <p className="flex items-center">
                    <Email className="mr-1 size-3" />
                    {item?.email || "--"}
                  </p>
                  <p className="flex items-center">
                    <Phone className="mr-1 size-3" />
                    {item?.contact_no || "--"}
                  </p>
                </div>
              ),
            };
          }) || []
        );
      }
    } catch (error) {
      toast?.error(error?.response?.data?.message);
    } finally {
      setIsEmpHistoryLoading(false);
    }
  };

  const docColumns = [
    {
      title: "Document Name",
      dataIndex: "original_name",
      render: (value) => value || "--",
    },
    {
      title: "Action",
      dataIndex: "path",
      render: (path) => (
        <p
          onClick={() => window.open(path, "_blank")}
          className="text-blue-500 underline cursor-pointer"
        >
          View
        </p>
      ),
    },
  ];

  const fetchEmployees = async () => {
    try {
      setIsEmpLoading(true);
      const { data } = await getClientWiseEmployees({ client_id: client?.id });

      setEmployeeData(
        data?.data?.map((item) => ({
          ...item,
          address: (
            <div className="w-[100px] truncate" title={item?.address}>
              {item?.address}
            </div>
          ),
          dob: item?.dob ? moment(item.dob).format("MMM DD, YYYY") : "--",
          joining_date: item?.dob
            ? moment(item.joining_date).format("MMM DD, YYYY")
            : "--",
          created_at: moment(item?.created_at).format("lll"),
          exemption: (
            <p
              className={` ${
                item?.exemption === "yes"
                  ? "text-green-500 font-bold capitalize"
                  : item?.exemption === "no"
                  ? "text-red-600 font-bold capitalize"
                  : ""
              }`}
            >
              {item?.exemption ? item.exemption : "--"}
            </p>
          ),

          action: (
            <GlobalTooltip title="View Payroll History">
              <button
                onClick={() => {
                  setSelectedEmployee(item);
                  setIsEmpOpen(true);
                }}
              >
                <View />
              </button>
            </GlobalTooltip>
          ),
        })) || []
      );
    } catch (error) {
      console.error("Error fetching employees:", error);
    } finally {
      setIsEmpLoading(false);
    }
  };

  useEffect(() => {
    getViewPayrollData();
    if (mode !== "employee") fetchEmployees();
  }, [client?.id]);

  return (
    <>
      <div className="bg-white p-5">
        {mode === "client" ? (
          <div>
            <div className="flex justify-between">
              <p className="font-semibold text-2xl">Payroll {data?.length ? `(${data?.length})` : null}</p>
              <div className="flex items-center justify-end mb-5">
                <LoadableButton
                  type="button"
                  variant="primary"
                  lable="View Employees"
                  onClick={() => setMode("employee")}
                />
              </div>
            </div>
            <SkeletonTable
              columns={columns}
              data={data}
              isLoading={isLoading}
              pagination={true}
            />
          </div>
        ) : (
          <div>
            <div className="flex gap-3 items-center mb-5">
              <button
                title="Back"
                className="text-secondary"
                onClick={() => setMode("client")}
              >
                <LeftArrow />
              </button>
              <p className="title">Employees</p>
            </div>
            <SkeletonTable
              columns={empColumns}
              data={employeeData}
              isLoading={isEmpLoading}
              pagination={true}
            />
          </div>
        )}
      </div>

      <ModalComponent
        isOpen={isEmployeeModalOpen}
        setIsOpen={setIsEmployeeModalOpen}
        title={
          <div>
            <p>View Payroll Employee History</p>
            <p className="text-base text-gray-500">
              [ {data[0]?.start_date} to {data[0]?.end_date} ]
            </p>
          </div>
        }
        width="60%"
      >
        <SkeletonTable
          rowKey="payroll_emp_id"
          columns={addPayrollColumns(setEmployeeHistory)}
          data={employeeHistory || []}
          isLoading={isEmpHistoryLoading}
        />
      </ModalComponent>

      <ModalComponent
        isOpen={docOpen}
        setIsOpen={setDocOpen}
        title="View Document"
      >
        <SkeletonTable
          columns={docColumns}
          data={docUrl || []}
          pagination={false}
        />
      </ModalComponent>

      <ModalComponent
        isOpen={isEmpOpen}
        setIsOpen={setIsEmpOpen}
        title="View All Payroll History"
        width={1000}
      >
        <PayrollHistory empData={selectedEmployee} />
      </ModalComponent>
    </>
  );
};

export default Payroll;
